import React from 'react';
import {useHistory} from 'react-router';
import {useFormik} from 'formik';
import * as yup from 'yup';
import {
  Container,
  Form,
  Header,
  Label,
  TextArea,
  Checkbox,
  Button,
} from 'semantic-ui-react';
import styled from 'styled-components';
import {useCreateContactMutation} from 'api';

const validateSchema = yup.object().shape({
  lastName: yup.string().trim().required('必須項目です'),
  firstName: yup.string().trim().required('必須項目です'),
  email: yup.string().trim().required('必須項目です'),
  message: yup.string().trim().required('必須項目です'),
  isCheck: yup.bool().oneOf([true], 'Field must be checked'),
});

export default () => {
  const history = useHistory();
  const [createContact, {loading}] = useCreateContactMutation({
    onCompleted: () => {
      history.push(`/contact/complete`);
    },
  });

  const formik = useFormik({
    validationSchema: validateSchema,
    initialValues: {
      lastName: '',
      firstName: '',
      email: '',
      tel: '',
      message: '',
      isCheck: false,
    },
    onSubmit: (values) => {
      if (confirm('送信してよろしいですか？')) {
        delete values.isCheck;
        createContact({variables: {attributes: values}});
      }
    },
  });

  return (
    <StyledContainer>
      <Form onSubmit={formik.handleSubmit}>
        <Title as="h1">お問い合わせ</Title>
        <Form.Group>
          <Form.Field required={true} width={8}>
            <StyledLabel>姓</StyledLabel>
            <StyledInput
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <Label basic color="red" pointing>
                {formik.errors.lastName}
              </Label>
            )}
          </Form.Field>
          <Form.Field required={true} width={8}>
            <StyledLabel>名</StyledLabel>
            <StyledInput
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <Label basic color="red" pointing>
                {formik.errors.firstName}
              </Label>
            )}
          </Form.Field>
        </Form.Group>

        <Form.Group>
          <Form.Field required={true} width={16}>
            <StyledLabel>メールアドレス</StyledLabel>
            <StyledInput
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email && (
              <Label basic color="red" pointing>
                {formik.errors.email}
              </Label>
            )}
          </Form.Field>
        </Form.Group>

        <Form.Field width={16}>
          <StyledLabel>電話番号</StyledLabel>
          <StyledInput
            name="tel"
            value={formik.values.tel}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.tel && formik.errors.tel && (
            <Label basic color="red" pointing>
              {formik.errors.tel}
            </Label>
          )}
        </Form.Field>
        <Form.Group>
          <Form.Field required={true} width={16}>
            <StyledLabel>お問い合わせ内容</StyledLabel>
            <StyledTextArea
              name="message"
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.message && formik.errors.message && (
              <Label basic color="red" pointing>
                {formik.errors.message}
              </Label>
            )}
          </Form.Field>
        </Form.Group>

        <Form.Group>
          <Form.Field className="agree">
            <Checkbox
              checked={!!formik.values.isCheck}
              onClick={(_, data) =>
                formik.setFieldValue('isCheck', data.checked)
              }
            />
            <span>
              <a href="/privacy" target="_blank">
                プライバシーポリシー
              </a>
              に同意する
            </span>
          </Form.Field>
        </Form.Group>

        <Actions>
          <StyledButton
            type="submit"
            disabled={!formik.dirty || !formik.isValid || loading}
            loading={loading}>
            送信する
          </StyledButton>
        </Actions>
      </Form>

      <Information>
        <p>お電話でのお問い合わせも受け付けています。</p>
        <TelInformation>
          <Tel>
            0848-63-5584<span>(平日 9:00 〜 17:30)</span>
          </Tel>
          <Notice>
            「ACミハラについて」と言っていただけるとスムーズです。
          </Notice>
        </TelInformation>
      </Information>
    </StyledContainer>
  );
};

const StyledContainer = styled(Container)`
  &&& {
    padding-top: 83px;
    width: 670px;
    margin-bottom: 80px;

    .agree {
      label {
        &:before {
          margin-top: 3px;
        }
      }
      span {
        margin-left: 5px;
      }
    }
  }
`;

const Title = styled(Header)`
  &&& {
    font-family: HiraginoSans-W3;
    font-weight: normal;
    font-size: 30px;
    letter-spacing: 0.5px;
    color: #747474;
    text-align: center;
  }
`;

const StyledLabel = styled.label`
  &&& {
    font-size: 12px !important;
    color: #aeaeae !important;
  }
`;

const StyledInput = styled(Form.Input)`
  &&& {
    input {
      border: solid 1px #f1f1f1 !important;
      background-color: #f5f5f5 !important;
    }
  }
`;

const StyledTextArea = styled(TextArea)`
  &&& {
    border: solid 1px #f1f1f1;
    background-color: #f5f5f5;
  }
`;

const Actions = styled.div`
  margin: 40px 0;
`;
const StyledButton = styled(Button)`
  &&& {
    display: block;
    margin: 0 auto;
    width: 240px;
    height: 56px;
    border-radius: 28.5px;
    color: #fff;
    background-color: #ef5350;

    &:hover,
    &:focus {
      &&& {
        color: #fff;
        background-color: #ef5350;
        border: 2px solid #ef5350;
        opacity: 0.5;
      }
    }
  }
`;

const Information = styled.div`
  margin-top: 80px;
  text-align: center;
  font-size: 16px;
  color: #212121;
`;

const TelInformation = styled.div`
  width: 654px;
  height: 130px;
  border: solid 1px #eeeeee;
  padding: 40px;
`;
const Tel = styled.div`
  font-size: 21px;
  letter-spacing: 1.9px;
  color: #424242;

  span {
    font-size: 15px;
  }
`;

const Notice = styled.div`
  font-size: 14px;
  color: #9e9e9e;
`;
